import FreeText from '@algbra/ui/components/FreeText/FreeText'
import { Container } from '@algbra/ui/components/Grid/Grid'
import Heading from '@algbra/ui/components/Heading/Heading'
import Whitespace from '@algbra/ui/components/Whitespace/Whitespace'
import { resolveAsAttachment, resolveAsImage } from '@algbra/ui/resolvers'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'
import styles from './ChoosePlans.module.scss'
import { ReactComponent as InfoIcon } from '../../images/info-grey.svg'

export type ChoosePlanItemProps = {
  id: string
  title: string
  subtitle?: string
  description?: string
  flagText?: string
  disclaimer?: string
  buttonTextColor?: string
  buttonText?: string
  buttonLink?: string
  buttonBgColorHex?: string
  flagImage?: GatsbyResolvedAsset
  cardImage?: GatsbyResolvedAsset
}

export type ZoneChoosePlansProps = {
  __typename: 'StrapiGQL_ComponentZonesChoosePlans'
  id: string
  choosePlanItem?: ChoosePlanItemProps[]
}

function ChoosePlanCard({
  title,
  subtitle,
  description,
  flagText,
  disclaimer,
  buttonTextColor,
  buttonText,
  buttonLink,
  buttonBgColorHex,
  flagImage,
  cardImage,
}: ChoosePlanItemProps) {
  const cardAsset = cardImage && resolveAsImage(cardImage)
  const flagAsset =
    flagImage && (resolveAsAttachment(flagImage) || flagImage.url)
  const buttonStyle = {
    backgroundColor: buttonBgColorHex || '#1a1a1a',
    color: buttonTextColor || '#ffffff',
  }

  return (
    <div className={styles.card}>
      {cardAsset && (
        <div className={styles.imageWrap}>
          <GatsbyImage image={cardAsset} alt={title} className={styles.image} />
        </div>
      )}
      {(flagText || flagAsset) && (
        <div className={styles.flag}>
          {flagAsset && (
            <img
              src={flagAsset}
              alt={flagText || title}
              className={styles.flagImage}
              loading="lazy"
            />
          )}
          {flagText && <span className={styles.flagText}>{flagText}</span>}
        </div>
      )}
      <div>
        <div className={styles.copy}>
          <Heading level={3} type="md" className={styles.cardTitle}>
            {title}
          </Heading>

          {subtitle && (
            <FreeText
              as="p"
              type="sm"
              innerHTML={subtitle}
              className={styles.subtitle}
            >
              {subtitle}
            </FreeText>
          )}

          {description && (
            <FreeText
              type="md"
              innerHTML={description}
              className={styles.description}
            >
              {description}
            </FreeText>
          )}
        </div>
      </div>
      {buttonText && buttonLink && (
        <Link to={buttonLink} className={styles.button} style={buttonStyle}>
          {buttonText}
        </Link>
      )}
      {disclaimer && (
        <div className={styles.disclaimerBlock}>
          <InfoIcon />
          <FreeText
            as="p"
            type="sm"
            innerHTML={disclaimer}
            className={styles.disclaimer}
            color="secondary"
          >
            {disclaimer}
          </FreeText>
        </div>
      )}
    </div>
  )
}

export default function ZoneChoosePlans({
  choosePlanItem,
}: ZoneChoosePlansProps): JSX.Element | null {
  if (!choosePlanItem?.length) {
    return null
  }

  return (
    <Whitespace type="lg">
      <Container>
        <section className={styles.section}>
          <div className={styles.grid}>
            {choosePlanItem.map((item, index) => (
              <ChoosePlanCard
                key={item.id || `${item.title}-${index}`}
                {...item}
              />
            ))}
          </div>
        </section>
      </Container>
    </Whitespace>
  )
}
